import { useState, useEffect, useRef, useMemo, useCallback } from "react";

import { useFormikContext } from "formik";
import { useSnackbar } from "notistack";
import { getPayrollItemAccounts, getAccounts } from "../../api";
import { useAlerts } from "../../Hooks";
import { filterAccounts, MAPPED_TO_BASE } from "./addDeductTaxUtils";
import { ITEM_TYPE } from "../../Enums/Enum";

/**
 * Custom hook that encapsulates all state and data-fetching logic
 * for EmployeeAddDeductTaxTables. Extracted from the monolithic component.
 */
export function useAddDeductTaxTable({
    row,
    formValues,
    corporationId,
    accountOptions,
    addDedItems,
    taxItems,
    onTotalsChange,
    refreshTrigger,
    onAddNewMappedItem,
    isReadOnly,
}) {
    const { values, setFieldValue, errors, touched } = useFormikContext();
    const valuesRef = useRef(values);
    useEffect(() => { valuesRef.current = values; }, [values]);

    const [removingIndex, setRemovingIndex] = useState(null);
    const [removingTaxIndex, setRemovingTaxIndex] = useState(null);

    const hasInitializedAddDed = useRef(false);
    const hasInitializedTaxes = useRef(false);

    const { enqueueSnackbar } = useSnackbar();
    const { popup: alertPopup, showDeleteConfirm, hidePopup } = useAlerts();

    // ─── Derived data ─────────────────────────────────────────────────────
    const addAndDeductions = values.addAndDeductions || [];
    const eeAndErTaxes = values.eeAndErTaxes || [];

    const importedEmpName = formValues?.importedName ?? values?.importedName ?? row?.raw?.ImportedEmployeeName ?? row?.importedName;
    const isImportedNameEmpty = !importedEmpName || String(importedEmpName).trim() === "" || String(importedEmpName).toLowerCase() === "null" || String(importedEmpName).toLowerCase() === "undefined";
    const isType1 = isImportedNameEmpty || values.importPayrollType === 1 || values?.isAddPayRoll;
    const showItemColumn = !isType1 && values.importPayrollType === 2;

    // ─── Account options ──────────────────────────────────────────────────
    const [localAccountOptions, setLocalAccountOptions] = useState([]);
    const [openAddCOA, setOpenAddCOA] = useState(false);
    const [pendingCOAField, setPendingCOAField] = useState("");

    useEffect(() => {
        setLocalAccountOptions(filterAccounts(accountOptions || []));
    }, [accountOptions]);

    const enhancedAccountOptions = useMemo(() => {
        const filtered = (localAccountOptions || []).filter(o => o.value !== "");
        return [{ label: "--Select--", value: "" }, { label: "Add New", value: "ADD_NEW" }, ...filtered];
    }, [localAccountOptions]);

    const accountOptionsMap = useMemo(() => {
        const map = new Map();
        enhancedAccountOptions.forEach(opt => {
            const strVal = String(opt.value || "");
            map.set(strVal, opt);
            map.set(strVal.toLowerCase(), opt);
            map.set(strVal.toUpperCase(), opt);
        });
        return map;
    }, [enhancedAccountOptions]);

    const validCOASet = useMemo(() => new Set((localAccountOptions || []).map(acc => String(acc.value))), [localAccountOptions]);

    // ─── Mapped-to options ────────────────────────────────────────────────
    const mappedToOptionsMap = useMemo(() => ({
        9: [...MAPPED_TO_BASE, ...(addDedItems || []).filter(x => Number(x.ItemType) === ITEM_TYPE.Additions)],
        10: [...MAPPED_TO_BASE, ...(addDedItems || []).filter(x => Number(x.ItemType) === ITEM_TYPE.Deductions)],
        11: [...MAPPED_TO_BASE, ...(taxItems || []).filter(x => Number(x.ItemType) === ITEM_TYPE.EmployeeTaxes)],
        12: [...MAPPED_TO_BASE, ...(taxItems || []).filter(x => Number(x.ItemType) === ITEM_TYPE.EmployerTaxes)],
    }), [addDedItems, taxItems]);

    const mappedToLookup = useMemo(() => {
        const map = {};
        Object.keys(mappedToOptionsMap).forEach(type => {
            const m = new Map();
            mappedToOptionsMap[type].forEach(o => m.set(String(o.ItemID || o.ItemId || o.Id || o.value || ""), o));
            map[type] = m;
        });
        return map;
    }, [mappedToOptionsMap]);

    // ─── Refresh accounts after QuickAdd COA ─────────────────────────────
    const refreshAccounts = useCallback(async () => {
        try {
            const freshAccounts = await getAccounts(
                corporationId || values?.corporationId || values?.corpId || "",
                "000000000000000000000000000000000000",
                true
            );
            const formattedAccounts = freshAccounts?.Accounts || freshAccounts || [];
            setLocalAccountOptions([{ label: "--Select--", value: "", type: "" }, ...filterAccounts(formattedAccounts)]);
        } catch (error) {
            console.error(error);
        }
    }, [corporationId, values?.corporationId, values?.corpId]);

    // ─── COA fetch on mapped-to selection ────────────────────────────────
    const fetchCoaDetails = useCallback(async (payrollItemId) => {
        if (!payrollItemId) return null;
        try {
            const corp = corporationId || values?.corporationId || values?.corporation?.corpValue || values?.corpId || "";
            const payload = {
                CorpID: corp,
                PayrollItemID: payrollItemId,
                IncDeptID: formValues?.department,
                JobID: formValues?.job,
            };
            const [data, freshAccounts] = await Promise.all([
                getPayrollItemAccounts(payload),
                getAccounts(corp),
            ]);
            let account = data?.PayrollItemAccounts?.[0] || data?.Items?.[0] || {};
            if (account?.AccountID) {
                const accountsArray = freshAccounts?.Accounts || freshAccounts || [];
                const isValid = accountsArray.some(acc => String(acc.ID || acc.value) === String(account.AccountID));
                if (!isValid) { account.AccountID = ""; account.AccountName = ""; }
            }
            return account;
        } catch (error) {
            console.error("Error fetching COA options:", error);
            return null;
        }
    }, [corporationId, values?.corporationId, values?.corporation?.corpValue, values?.corpId, formValues?.department, formValues?.job]);

    // ─── Refresh trigger (QuickAdd success) ───────────────────────────────
    useEffect(() => {
        const processRefresh = async () => {
            if (!refreshTrigger?.trigger) return;
            const { id, rowIndex, arrayName } = refreshTrigger;
            if (!id || rowIndex === undefined || !arrayName) return;

            const updatedArray = [...values[arrayName]];
            const typeId = updatedArray[rowIndex]?.addDeduction || updatedArray[rowIndex]?.taxType;
            const account = await fetchCoaDetails(id);
            const options = mappedToOptionsMap[typeId] || [];
            const matchedItem = options.find(o => String(o.ItemID) === String(id));
            const isErTax = Number(typeId) === ITEM_TYPE.EmployerTaxes;
            const fetchedCoa = account?.AccountID || matchedItem?.AccountID || matchedItem?.COA || "";
            const fetchedCoaName = account?.AccountName || matchedItem?.AccountName || matchedItem?.COAName || "";
            const fetchedExpenseCoa = account?.LiabilityAccountID || account?.ExpenseAccountID || account?.ExpenseCOA || account?.PayableCOA ||
                matchedItem?.LiabilityAccountID || matchedItem?.ExpenseAccountID || matchedItem?.ExpenseCOA || matchedItem?.PayableCOA || (isErTax ? fetchedCoa : "");

            updatedArray[rowIndex] = {
                ...updatedArray[rowIndex],
                mappedTo: id,
                mappedToName: matchedItem?.PayrollItemName || matchedItem?.ItemName || "",
                coa: fetchedCoa,
                coaName: fetchedCoa ? fetchedCoaName : "",
                expenseCoa: isErTax ? (fetchedExpenseCoa || fetchedCoa || "") : ""
            };
            setFieldValue(arrayName, updatedArray);
        };
        processRefresh();
    }, [refreshTrigger]);

    // ─── Initialize empty rows ────────────────────────────────────────────
    useEffect(() => {
        if (!hasInitializedAddDed.current) {
            if (addAndDeductions.length === 0) {
                setFieldValue("addAndDeductions", [{
                    id: Date.now(), addDeduction: "", item: "", mappedTo: "", mappedToName: "", coa: "", coaName: "", amount: "0.00"
                }]);
            }
            hasInitializedAddDed.current = true;
        }
    }, [setFieldValue]);

    useEffect(() => {
        if (!hasInitializedTaxes.current) {
            if (eeAndErTaxes.length === 0) {
                setFieldValue("eeAndErTaxes", [{
                    id: Date.now() + 1, taxType: "", item: "", mappedTo: "", mappedToName: "", coa: "", coaName: "", amount: "0.00"
                }]);
            }
            hasInitializedTaxes.current = true;
        }
    }, [setFieldValue]);

    // ─── Totals sync ──────────────────────────────────────────────────────
    useEffect(() => {
        const totalAdditions = addAndDeductions.reduce(
            (sum, r) => sum + (Number(r.addDeduction) === Number(ITEM_TYPE.Additions) ? Number(r.amount || 0) : 0), 0
        );
        const totalDeductions = addAndDeductions.reduce(
            (sum, r) => sum + (Number(r.addDeduction) === Number(ITEM_TYPE.Deductions) ? Number(r.amount || 0) : 0), 0
        );
        const totalEETaxes = eeAndErTaxes.reduce(
            (sum, r) => sum + (Number(r.taxType) === Number(ITEM_TYPE.EmployeeTaxes) ? Number(r.amount || 0) : 0), 0
        );
        const totalERTaxes = eeAndErTaxes.reduce(
            (sum, r) => sum + (Number(r.taxType) === Number(ITEM_TYPE.EmployerTaxes) ? Number(r.amount || 0) : 0), 0
        );
        onTotalsChange?.({ totalAdditions, totalDeductions, totalEETaxes, totalERTaxes });
    }, [addAndDeductions, eeAndErTaxes]); // eslint-disable-line


    // ─── Currency formatter ───────────────────────────────────────────────
    const formatCurrency = useCallback((val) => {
        return val < 0
            ? `-$${Math.abs(val).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : `$${Number(val).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }, []);

    return {
        values, setFieldValue, errors, touched,
        enqueueSnackbar,
        removingIndex, setRemovingIndex,
        removingTaxIndex, setRemovingTaxIndex,
        alertPopup, showDeleteConfirm, hidePopup,
        openAddCOA, setOpenAddCOA,
        pendingCOAField, setPendingCOAField,
        isType1, showItemColumn,
        addAndDeductions, eeAndErTaxes,
        mappedToOptionsMap, mappedToLookup,
        enhancedAccountOptions, accountOptionsMap, validCOASet,
        fetchCoaDetails,
        refreshAccounts,
        formatCurrency,
    };
}
